import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

const STATUS_CONFIG = {
  ready:       { label: '구매준비',         color: '#534AB7', bg: '#EEEDFE', allowed: ['buy','hold','cancel'] },
  hold:        { label: '구매보류',         color: '#BA7517', bg: '#FAEEDA', allowed: ['ready','buy','cancel'] },
  buy:         { label: '구매완료',         color: '#5F5E5A', bg: '#F1EFE8', allowed: ['ready','ship','claim','cancel'] },
  ship:        { label: '배송중',           color: '#185FA5', bg: '#E6F1FB', allowed: ['ready','done','claim','cancel'] },
  done:        { label: '배송완료',         color: '#0F6E56', bg: '#E1F5EE', allowed: ['ready','claim','cancel'] },
  claim:       { label: '반품·교환 진행중', color: '#854F0B', bg: '#FAEEDA', allowed: ['done','cancel','refund_done'] },
  refund_done: { label: '반품·교환 완료',   color: '#5F5E5A', bg: '#F1EFE8', allowed: ['claim'] },
  cancel:      { label: '주문취소',         color: '#A32D2D', bg: '#FCEBEB', allowed: ['ready','buy','ship','done','claim'] },
};

const BORDER_COLORS = {
  ready: '#534AB7', hold: '#EF9F27', buy: '#888780', ship: '#185FA5',
  done: '#1D9E75', claim: '#EF9F27', refund_done: '#888780', cancel: '#E24B4A'
};


const COURIER_MAP = {
  CJ: 'kr.cjlogistics',
  LOTTE: 'kr.lotte',
  HANJIN: 'kr.hanjin',
  LOGEN: 'kr.logen',
  EPOST: 'kr.epost',
  CHUNILPS: 'kr.chunilps',
  CUPOST: 'kr.cupost',
};

const COURIER_TRACK_URL = {
  CJ: (no) => `https://trace.cjlogistics.com/web/info.jsp?slipno=${no}`,
  LOTTE: (no) => `https://www.lotteglogis.com/home/reservation/tracking/linkView?InvNo=${no}`,
  HANJIN: (no) => `https://www.hanjin.com/kor/CMS/DeliveryMgr/WaybillResult.do?mCode=MN038&schLang=KR&wblnumText=&wblnum=${no}`,
  LOGEN: (no) => `https://www.ilogen.com/web/personal/trace/${no}`,
  EPOST: (no) => `https://service.epost.go.kr/trace.RetrieveDomRigiTraceList.comm?sid1=${no}`,
  CHUNILPS: (no) => `https://www.chunil.co.kr/HTrace/HTrace.jsp?transNo=${no}`,
  CUPOST: (no) => `https://www.cupost.co.kr/shopping_rest/tranStatReq.cupost${no}`,
  KURLYNEXTMILE: (no) => `https://www.kurlynextmile.com/information/invoice?invoice_no=${no}`,
};

const api = axios.create({ baseURL: '/api' });
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// 판매처별 상품 페이지 URL 생성. 아직 지원 안 하는 판매처는 null 대신 안내 메시지를 반환.
const UNSUPPORTED_PLATFORMS_COMING_SOON = [];
function getPlatformProductLink(platform, item) {
  if (platform === '스마트스토어') {
    if (item.naver_channel_product_no) {
      return { url: `https://smartstore.naver.com/byeolmanshop/products/${item.naver_channel_product_no}` };
    }
    return null;
  }
  if (platform === '11번가') {
    if (item.eleven_product_no) {
      return { url: `https://www.11st.co.kr/products/${item.eleven_product_no}` };
    }
    return null;
  }
  if (platform === '지마켓') {
    if (item.gmarket_goods_no) {
      return { url: `https://item.gmarket.co.kr/Item?goodscode=${item.gmarket_goods_no}` };
    }
    return null;
  }
  if (platform === '옥션') {
    if (item.auction_goods_no) {
      return { url: `https://itempage3.auction.co.kr/DetailView.aspx?ItemNo=${item.auction_goods_no}&frm3=V2` };
    }
    return null;
  }
  if (platform === '셀러허브') {
    // [참고] 셀러허브 상품 상세페이지는 셀러허브 관리자 계정으로 로그인되어 있어야 정상적으로 볼 수 있다.
    if (item.sellerhub_goods_no) {
      return { url: `https://admin.sellerhub.co.kr/shop/goods/goods_view.php?goodsno=${item.sellerhub_goods_no}` };
    }
    return null;
  }
  if (platform === '쿠팡') {
    if (item.coupang_product_id && item.coupang_vendor_item_id) {
      return { url: `https://www.coupang.com/vp/products/${item.coupang_product_id}?vendorItemId=${item.coupang_vendor_item_id}` };
    }
    return null;
  }
  if (UNSUPPORTED_PLATFORMS_COMING_SOON.includes(platform)) {
    return { message: '차후 지원 예정입니다.' };
  }
  return null;
}

function StatusPill({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.ready;
  return (
    <span style={{ fontSize: '11px', padding: '3px 9px', borderRadius: '8px', background: cfg.bg, color: cfg.color, fontWeight: '500', display: 'inline-block', whiteSpace: 'nowrap' }}>{cfg.label}</span>
  );
}

function LinkEditor({ item, urlField, apiField, label, color }) {
  const [editing, setEditing] = useState(false);
  const [url, setUrl] = useState(item[urlField] || '');
  const [savedUrl, setSavedUrl] = useState(item[urlField] || '');

  const handleSave = async () => {
    try {
      await api.patch(`/products/${item.product_id}/${apiField}`, { [apiField.replace(/-/g, '_')]: url });
      setSavedUrl(url);
      setEditing(false);
    } catch (err) { alert('저장 실패'); }
  };

  if (!item.product_id) return null;

  return (
    <div style={{ display: 'inline-block', marginRight: '6px' }}>
      {editing ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://"
            style={{ fontSize: '10px', padding: '3px 7px', borderRadius: '4px', border: `0.5px solid ${color}`, width: '240px' }} autoFocus />
          <button onClick={handleSave} style={{ fontSize: '10px', padding: '3px 8px', borderRadius: '4px', border: 'none', background: '#1D9E75', color: '#fff', cursor: 'pointer' }}>저장</button>
          <button onClick={() => { setEditing(false); setUrl(savedUrl); }} style={{ fontSize: '10px', padding: '3px 8px', borderRadius: '4px', border: '0.5px solid #ddd', background: '#fff', cursor: 'pointer' }}>취소</button>
        </div>
      ) : (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          {savedUrl ? (
            <a href={savedUrl} target="_blank" rel="noreferrer"
              style={{ fontSize: '10px', color, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '3px', border: `0.5px solid ${color}`, borderRadius: '4px', padding: '2px 7px', opacity: 0.8 }}>
              {label}
            </a>
          ) : (
            <span style={{ fontSize: '10px', color: '#aaa' }}>{label} 없음</span>
          )}
          <button onClick={() => setEditing(true)} style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', border: '0.5px solid #ddd', background: '#fff', color: '#666', cursor: 'pointer' }}>✏</button>
        </div>
      )}
    </div>
  );
}

function PriceEditor({ item }) {
  const [editing, setEditing] = useState(false);
  const [price, setPrice] = useState(item.expected_price || 0);
  const [savedPrice, setSavedPrice] = useState(item.expected_price || 0);

  const handleSave = async () => {
    try {
      await api.patch(`/products/${item.product_id}/expected-price`, { expected_price: parseInt(price) || 0 });
      setSavedPrice(parseInt(price) || 0);
      setEditing(false);
    } catch (err) { alert('저장 실패'); }
  };

  if (!item.product_id) return null;

  return (
    <div style={{ display: 'inline-block', marginRight: '6px' }}>
      {editing ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <input type="number" value={price} onChange={e => setPrice(e.target.value)}
            style={{ fontSize: '10px', padding: '3px 7px', borderRadius: '4px', border: '0.5px solid #EF9F27', width: '100px' }} autoFocus />
          <span style={{ fontSize: '10px', color: '#666' }}>원</span>
          <button onClick={handleSave} style={{ fontSize: '10px', padding: '3px 8px', borderRadius: '4px', border: 'none', background: '#1D9E75', color: '#fff', cursor: 'pointer' }}>저장</button>
          <button onClick={() => { setEditing(false); setPrice(savedPrice); }} style={{ fontSize: '10px', padding: '3px 8px', borderRadius: '4px', border: '0.5px solid #ddd', background: '#fff', cursor: 'pointer' }}>취소</button>
        </div>
      ) : (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ fontSize: '10px', color: '#EF9F27', border: '0.5px solid #EF9F27', borderRadius: '4px', padding: '2px 7px' }}>
            💰 기준가 {savedPrice.toLocaleString()}원
          </span>
          <button onClick={() => setEditing(true)} style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', border: '0.5px solid #ddd', background: '#fff', color: '#666', cursor: 'pointer' }}>✏</button>
        </div>
      )}
    </div>
  );
}

function RefundButton({ item, order, onRefundSave }) {
  const [showModal, setShowModal] = useState(false);
  return (
    <>
      <button onClick={() => setShowModal(true)} style={{ fontSize: '10px', padding: '2px 10px', borderRadius: '4px', border: 'none', background: '#E24B4A', color: '#fff', cursor: 'pointer', fontWeight: '500', marginLeft: '4px' }}>
        반품 처리
      </button>
      {showModal && (
        <RefundModal item={item} order={order} onClose={() => setShowModal(false)} onSave={onRefundSave} />
      )}
    </>
  );
}

function RefundModal({ item, order, onClose, onSave }) {
  const salePrice = item.sale_price || 0;
  const origPurchase = item.purchase_record?.purchase_price || 0;

  const [form, setForm] = useState({
    sale_refund_fee: 0,
    purchase_refund_fee: 0,
    supplier_possible: true,
  });

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const customerRefund = salePrice - form.sale_refund_fee;
  const supplierRefund = form.supplier_possible ? origPurchase - form.purchase_refund_fee : 0;
  const customerPnl = form.sale_refund_fee;
  const supplierPnl = supplierRefund - origPurchase;
  const total = supplierPnl;

  const handleSave = async () => {
    try {
      await api.patch('/orders/' + order.id + '/status', { status: 'refund_done' });
      await api.patch('/orders/items/' + item.id + '/status', { status: 'refund_done' });
      if (total !== 0) {
        const pr = item.purchase_record;
        if (pr) {
          await api.post('/purchases/' + item.id, {
            purchase_platform: pr.purchase_platform || '',
            purchase_account: pr.purchase_account || '',
            purchase_order_number: pr.purchase_order_number || '',
            cart_number: pr.cart_number || '',
            purchase_price: pr.purchase_price || 0,
            shipping_cost: pr.shipping_cost || 0,
            extra_cost: total,
            card_name: pr.card_name || '국민4044',
          });
        }
      }
      onSave();
      onClose();
    } catch (err) { alert('저장 실패'); }
  };

  const inp = { fontSize: '12px', padding: '6px 10px', borderRadius: '6px', border: '0.5px solid #ddd', width: '100%', textAlign: 'right', boxSizing: 'border-box' };
  const inpRO = { ...inp, background: '#f5f5f5', color: '#aaa' };
  const sl2 = { fontSize: '10px', color: '#888', marginBottom: '3px' };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#fff', borderRadius: '14px', width: '520px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
        <div style={{ padding: '16px 20px 14px', borderBottom: '0.5px solid #e8e8e8', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: '#fff', borderRadius: '14px 14px 0 0', zIndex: 1 }}>
          <div style={{ fontSize: '14px', fontWeight: '500', color: '#A32D2D', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '3px', height: '16px', background: '#E24B4A', borderRadius: '2px' }} />
            반품 처리
          </div>
          <button onClick={onClose} style={{ fontSize: '18px', color: '#aaa', background: 'none', border: 'none', cursor: 'pointer', lineHeight: 1, padding: '2px 6px' }}>✕</button>
        </div>
        <div style={{ padding: '16px 20px' }}>
          <div style={{ background: '#f5f5f5', borderRadius: '8px', padding: '10px 12px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
            <span style={{ fontWeight: '500', color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{item.product_name}</span>
            <span style={{ color: '#666', flexShrink: 0, marginLeft: '10px' }}>판매금 {salePrice.toLocaleString()}원 · 구입비 {origPurchase.toLocaleString()}원</span>
          </div>
          <div style={{ fontSize: '11px', color: '#666', background: '#f9f9f9', padding: '8px 12px', borderRadius: '8px', marginBottom: '14px', lineHeight: '1.6' }}>
            반품비는 판매금액/구입비에서 차감됩니다.<br/>반품비를 입력하면 손익이 자동 계산됩니다.<br/>
            <span style={{ color: '#854F0B' }}>💡 보상금/배상금이 있는 경우, 반품 완료 후 추가액을 직접 수정해주세요.</span><br/>
            <span style={{ color: '#aaa' }}>(예: 구입비 손실 -27,810 + 보상금 7,000 = 추가액 -20,810)</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
            <div style={{ border: '0.5px solid #e8e8e8', borderRadius: '8px', overflow: 'hidden' }}>
              <div style={{ padding: '8px 12px', background: '#E1F5EE', fontSize: '11px', fontWeight: '500', color: '#0F6E56', textAlign: 'center' }}>판매 (고객)</div>
              <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div><div style={sl2}>판매금액</div><input style={inpRO} value={salePrice.toLocaleString()} readOnly /></div>
                <div><div style={sl2}>판매 반품비</div><input style={inp} type="number" value={form.sale_refund_fee} onChange={e => update('sale_refund_fee', parseInt(e.target.value)||0)} placeholder="0" /></div>
                <div><div style={sl2}>고객 환불금액 (자동)</div><input style={inpRO} value={customerRefund.toLocaleString()} readOnly /></div>
              </div>
            </div>
            <div style={{ border: '0.5px solid #e8e8e8', borderRadius: '8px', overflow: 'hidden' }}>
              <div style={{ padding: '8px 12px', background: '#E6F1FB', fontSize: '11px', fontWeight: '500', color: '#185FA5', textAlign: 'center' }}>구매 (구매처)</div>
              <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div><div style={sl2}>원 구입비</div><input style={inpRO} value={origPurchase.toLocaleString()} readOnly /></div>
                <div><div style={sl2}>구매 반품비</div><input style={{ ...inp, background: form.supplier_possible ? '#fff' : '#f5f5f5', color: form.supplier_possible ? '#1a1a1a' : '#aaa' }} type="number" value={form.purchase_refund_fee} onChange={e => update('purchase_refund_fee', parseInt(e.target.value)||0)} placeholder="0" readOnly={!form.supplier_possible} /></div>
                <div><div style={sl2}>구입비 회수금액 (자동)</div><input style={inpRO} value={supplierRefund.toLocaleString()} readOnly /></div>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', padding: '10px 12px', background: '#f9f9f9', borderRadius: '8px' }}>
            <span style={{ fontSize: '12px', color: '#666' }}>구매처 반품</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', cursor: 'pointer' }}>
              <input type="radio" name="supplier_possible" checked={form.supplier_possible} onChange={() => update('supplier_possible', true)} style={{ accentColor: '#1D9E75' }} />
              <span style={{ color: '#1D9E75', fontWeight: '500' }}>가능</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', cursor: 'pointer' }}>
              <input type="radio" name="supplier_possible" checked={!form.supplier_possible} onChange={() => { update('supplier_possible', false); update('purchase_refund_fee', 0); }} style={{ accentColor: '#E24B4A' }} />
              <span style={{ color: '#E24B4A', fontWeight: '500' }}>불가 (구입비 손실)</span>
            </label>
          </div>
          <div style={{ background: '#f5f5f5', borderRadius: '8px', padding: '12px 14px', marginBottom: '14px' }}>
            <div style={{ fontSize: '11px', color: '#888', marginBottom: '10px', fontWeight: '500' }}>손익 계산 (자동)</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '8px' }}>
              {[
                { label: '고객 반품비 수취', value: (customerPnl>=0?'+':'')+customerPnl.toLocaleString(), color: customerPnl>=0?'#1D9E75':'#E24B4A' },
                { label: '구입비 손실/회수', value: (supplierPnl>=0?'+':'')+supplierPnl.toLocaleString(), color: supplierPnl>=0?'#1D9E75':'#E24B4A' },
                { label: '최종 손익', value: (total>=0?'+':'')+total.toLocaleString(), color: total>0?'#1D9E75':total<0?'#E24B4A':'#888' },
              ].map((r,i) => (
                <div key={i} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '10px', color: '#aaa', marginBottom: '3px' }}>{r.label}</div>
                  <div style={{ fontSize: '13px', fontWeight: '500', color: r.color }}>{r.value}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ padding: '8px 12px', borderRadius: '8px', fontSize: '11px', marginBottom: '14px', background: total>0?'#E6F1FB':total<0?'#FAEEDA':'#f5f5f5', color: total>0?'#185FA5':total<0?'#854F0B':'#888' }}>
            {total!==0 ? `추가액 ${total>0?'+':''}${total.toLocaleString()}원이 주문에 반영됩니다.` : '손익이 0원입니다. 추가액 변동 없습니다.'}
          </div>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={{ fontSize: '12px', padding: '8px 20px', borderRadius: '8px', border: '0.5px solid #ddd', background: '#fff', color: '#666', cursor: 'pointer' }}>취소</button>
            <button onClick={handleSave} style={{ fontSize: '12px', padding: '8px 20px', borderRadius: '8px', border: 'none', background: '#E24B4A', color: '#fff', cursor: 'pointer', fontWeight: '500' }}>반품 처리 완료</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PurchaseSection({ item, order, itemIndex, platforms, accounts, cards, couriers, trackerClientId, onSave }) {
  const pr = item.purchase_record || {};
  const [form, setForm] = useState({
    purchase_platform: pr.purchase_platform || '',
    purchase_account: pr.purchase_account || '',
    purchase_order_number: pr.purchase_order_number || '',
    cart_number: pr.cart_number || '',
    purchase_price: pr.purchase_price || item.expected_price || '',
    shipping_cost: pr.shipping_cost || 0,
    extra_cost: pr.extra_cost || 0,
    card_name: pr.card_name || '국민4044',
  });
  const [savedTime, setSavedTime] = useState('');
  const [tracking, setTracking] = useState({
    courier: pr.courier || '',
    tracking_number: pr.tracking_number || '',
  });
  const handleTrackingSave = async () => {
    if (!tracking.courier || !tracking.tracking_number) return alert('택배사와 송장번호를 입력해주세요.');
    try {
      const axios = (await import('axios')).default;
      await axios.patch(`/api/purchases/${item.id}/tracking`, tracking, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      alert('송장이 등록됐습니다. 주문이 배송중으로 변경됩니다.');
    } catch(e) { alert('송장 등록 실패'); }
  };

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    await onSave(item.id, form);
    const now = new Date();
    const p = n => n.toString().padStart(2,'0');
    const ts = now.getFullYear()+'.'+p(now.getMonth()+1)+'.'+p(now.getDate())+' '+p(now.getHours())+':'+p(now.getMinutes())+':'+p(now.getSeconds());
    setSavedTime(ts);
  };

  const selStyle = { width: '100%', fontSize: '12px', padding: '4px 7px', borderRadius: '6px', border: '0.5px solid #ddd', background: '#fff' };
  const inputStyle = { width: '100%', fontSize: '12px', padding: '4px 7px', borderRadius: '6px', border: '0.5px solid #ddd', boxSizing: 'border-box', MozAppearance: 'textfield' };
  const sl = { fontSize: '11px', color: '#888', marginBottom: '3px' };

  const settlement = (item.settlement_price || 0) + (itemIndex === 0 ? (order.shipping_settlement || 0) : 0);
  const salePrice = (item.sale_price || 0) + (itemIndex === 0 ? (order.shipping_fee || 0) : 0);
  const fee = item.fee_amount > 0 ? item.fee_amount : salePrice - settlement;
  const feeRate = item.fee_rate > 0 ? Number(item.fee_rate).toFixed(2) : (salePrice ? ((fee / salePrice) * 100).toFixed(2) : 0);
  const purchasePrice = parseInt(form.purchase_price || 0);
  const shippingCost = parseInt(form.shipping_cost || 0);
  const extraCost = parseInt(form.extra_cost || 0);
  const isCancelled = order.status === 'cancel' || item.item_status === 'refund_done';
  const margin = isCancelled
    ? extraCost
    : settlement - purchasePrice - shippingCost - extraCost;
  // isCancelled 시 판매금/서비스료/정산금/구매가 모두 0 → 마진 = 추가액

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '580px 170px 1fr', borderBottom: '0.5px solid #e8e8e8' }}>
      <div style={{ padding: '12px 14px', borderRight: '0.5px solid #e8e8e8' }}>
        {/* 1줄: 5단 */}
        <div style={{ display: 'grid', gridTemplateColumns: '80px 80px 140px 90px 90px', gap: '5px 8px', marginBottom: '6px' }}>
          <div><div style={sl}>구매처</div><select style={selStyle} value={form.purchase_platform} onChange={e => {
            const platform = e.target.value;
            update('purchase_platform', platform);
            const autoMap = { '지마켓': 'renski6802', '풀무원': 'jinha111', '옥션': 'juk3290', 'SSG': 'juk3290', '네이버': 'ruddn6808', '11번가': 'juk3290@naver.com' };
            if (autoMap[platform]) update('purchase_account', autoMap[platform]);
          }}><option value="">--선택--</option>{platforms.map(p => <option key={p.id} value={p.code}>{p.name_kr}</option>)}</select></div>
          <div><div style={sl}>구매 아이디</div><select style={selStyle} value={form.purchase_account} onChange={e => update('purchase_account', e.target.value)}><option value="">--선택--</option>{accounts.map(a => <option key={a.id} value={a.account_id}>{a.account_id}</option>)}</select></div>
          <div><div style={sl}>구매주문번호</div><input style={inputStyle} value={form.purchase_order_number} onChange={e => update('purchase_order_number', e.target.value)} placeholder="구매처 주문번호" /></div>
          <div>
            <div style={sl}>카트번호</div>
            <input style={inputStyle} value={form.cart_number} onChange={e => update('cart_number', e.target.value)} placeholder="카트번호" />
            <div style={{ fontSize: '10px', color: '#aaa', marginTop: '2px' }}>※ 지마켓·풀무원 구매 시 입력</div>
          </div>
          <div><div style={sl}>카드</div><select style={selStyle} value={form.card_name} onChange={e => update('card_name', e.target.value)}><option value="">--선택--</option>{cards.map(c => <option key={c.id} value={c.card_name}>{c.card_name}</option>)}</select></div>
        </div>
        {/* 2줄: 3단 */}
        <div style={{ display: 'grid', gridTemplateColumns: '90px 90px 90px', gap: '5px 8px', marginBottom: '6px' }}>
          <div><div style={sl}>구매가</div><input type="number" style={inputStyle} value={form.purchase_price} onChange={e => update('purchase_price', e.target.value)} placeholder="0" /></div>
          <div><div style={sl}>배송비</div><input type="number" style={inputStyle} value={form.shipping_cost} onChange={e => update('shipping_cost', e.target.value)} placeholder="0" /></div>
          <div><div style={sl}>추가액</div><input type="number" style={inputStyle} value={form.extra_cost} onChange={e => update('extra_cost', e.target.value)} placeholder="0" /></div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingTop: '8px', borderTop: '0.5px solid #e8e8e8' }}>
          <button onClick={handleSave} style={{ fontSize: '12px', padding: '5px 16px', borderRadius: '6px', border: 'none', background: '#1D9E75', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>저장</button>
          <span style={{ fontSize: '11px', color: savedTime ? '#1a1a1a' : '#aaa' }}>
            {savedTime ? `구매일 ${savedTime}` : (pr.purchased_at ? new Date(pr.purchased_at).toLocaleString('ko-KR') : '저장 시 구매일 자동 입력')}
          </span>
          {pr.purchase_platform && (() => {
            const platform = form.purchase_platform || pr.purchase_platform;
            const orderNo = form.purchase_order_number || pr.purchase_order_number;
            const cartNo = form.cart_number || pr.cart_number;
            let url = null;
            if (platform === '11번가') url = 'https://buy.11st.co.kr/my11st/order/OrderList.tmall';
            else if (platform === '지마켓' && cartNo) url = `https://my.gmarket.co.kr/ko/pc/detail/basic/${cartNo}`;
            else if (platform === '옥션' && orderNo) url = `https://escrow.auction.co.kr/Close/OrderProcessDetailLayer.aspx?order_no=${orderNo}`;
            else if (platform === '네이버' && orderNo) url = `https://orders.pay.naver.com/order/status/${orderNo}`;
            else if (platform === 'CJ더마켓' && orderNo) url = `https://www.cjthemarket.com/pc/myOrder/orderDetail?odrNo=${orderNo}`;
            else if (platform === '컬리' && orderNo) url = `https://www.kurly.com/mypage/order/${orderNo}`;
            else if (platform === '롯데온' && orderNo) url = `https://www.lotteon.com/p/order/claim/orderDetail?odNo=${orderNo}`;
            else if (platform === 'SSG' && orderNo) url = `https://pay.ssg.com/myssg/orderInfoDetail.ssg?orordNo=${orderNo}&viewType=Ssg`;
            else if (platform === '풀무원' && cartNo) url = `https://shop.pulmuone.co.kr/mypage/orderDetail?order=${cartNo}&before=orderHistory`;
            return (
              <button onClick={() => {
                if (url) window.open(url, '_blank');
                else alert('미구현입니다. 수동으로 확인해주세요.');
              }} style={{ fontSize: '12px', padding: '5px 12px', borderRadius: '6px', border: '0.5px solid #378ADD', background: '#fff', color: '#378ADD', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>구매링크이동</button>
            );
          })()}
        </div>
      </div>
      <div style={{ padding: '12px 14px', background: '#fafafa', borderRight: '0.5px solid #e8e8e8', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <div><div style={sl}>택배사</div><select style={selStyle} value={tracking.courier} onChange={e => setTracking(t => ({ ...t, courier: e.target.value }))}><option value="">--선택--</option>{(couriers||[]).map(c => <option key={c.id} value={c.code}>{c.name}</option>)}</select></div>
        <div><div style={sl}>송장번호</div><input placeholder="송장번호 입력" style={inputStyle} value={tracking.tracking_number} onChange={e => setTracking(t => ({ ...t, tracking_number: e.target.value }))} /></div>
          <div><div style={sl}>배송 상태</div>
            {tracking.courier && tracking.tracking_number ? (
                <a href={COURIER_TRACK_URL[tracking.courier] ? COURIER_TRACK_URL[tracking.courier](tracking.tracking_number) : `https://link.tracker.delivery/track?client_id=${trackerClientId}&carrier_id=${COURIER_MAP[tracking.courier] || tracking.courier}&tracking_number=${tracking.tracking_number}`}
                target="_blank" rel="noreferrer"
                style={{ fontSize: '12px', padding: '4px 10px', borderRadius: '6px', border: '0.5px solid #185FA5', background: '#E6F1FB', color: '#185FA5', textDecoration: 'none', display: 'inline-block', marginTop: '2px' }}>
                🚚 배송조회
              </a>
            ) : (
              <input readOnly placeholder="추적 후 표시" style={{ ...inputStyle, background: '#f0f0f0', color: '#aaa' }} />
            )}
          </div>
        <div style={{ textAlign: 'right', marginTop: '2px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px' }}>
          {pr.invoice_sent === true && (
            <span style={{ fontSize: '10px', padding: '2px 7px', borderRadius: '4px', background: '#E1F5EE', color: '#0F6E56', border: '0.5px solid #1D9E75', fontWeight: '500' }}>전송완료</span>
          )}
          {pr.invoice_sent === false && pr.tracking_number && (
            <span style={{ fontSize: '10px', padding: '2px 7px', borderRadius: '4px', background: '#FCEBEB', color: '#A32D2D', border: '0.5px solid #E24B4A', fontWeight: '500' }}>미전송</span>
          )}
          <button onClick={handleTrackingSave} style={{ fontSize: '12px', padding: '5px 14px', borderRadius: '6px', border: 'none', background: '#1D9E75', color: '#fff', cursor: 'pointer' }}>송장 등록</button>
        </div>
      </div>
      <div style={{ padding: '12px 14px', background: '#fafafa' }}>
        {order.status === 'claim' && order.is_exchange && (
          <div style={{ marginBottom: '8px', padding: '4px 10px', background: '#E6F1FB', borderRadius: '6px', fontSize: '11px', color: '#185FA5', fontWeight: '500' }}>
            교환처리중
          </div>
        )}
        {(item.item_status === 'refund_done' || (order.status === 'refund_done' && order.is_exchange)) && (
          <div style={{ marginBottom: '8px', padding: '4px 10px', background: '#FAEEDA', borderRadius: '6px', fontSize: '11px', color: '#854F0B', fontWeight: '500', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{order.is_exchange ? '교환처리됨' : '반품처리됨'}</span>
            <button onClick={async () => {
              const statusOptions = [
                { value: 'ship', label: '배송중' },
                { value: 'done', label: '배송완료' },
              ];
              const statusLabel = statusOptions.map((s,i) => `${i+1}. ${s.label}`).join('\n');
              const choice = window.prompt(`되돌릴 상태를 선택하세요:\n${statusLabel}\n\n번호를 입력하세요 (1 또는 2):`);
              if (!choice) return;
              const selected = statusOptions[parseInt(choice)-1];
              if (!selected) { alert('올바른 번호를 입력하세요.'); return; }
              if (!window.confirm(`반품처리를 되돌리시겠습니까?\n상태: ${selected.label}\n추가액이 0으로 초기화됩니다.`)) return;
              try {
                await api.patch('/orders/' + order.id + '/status', { status: selected.value });
                await api.patch('/orders/' + order.id + '/exchange-reset');
                await api.patch('/orders/items/' + item.id + '/status', { status: 'normal' });
                const pr = item.purchase_record;
                if (pr) {
                  await api.post('/purchases/' + item.id, {
                    purchase_platform: pr.purchase_platform || '',
                    purchase_account: pr.purchase_account || '',
                    purchase_order_number: pr.purchase_order_number || '',
                    cart_number: pr.cart_number || '',
                    purchase_price: pr.purchase_price || 0,
                    shipping_cost: pr.shipping_cost || 0,
                    extra_cost: 0,
                    card_name: pr.card_name || '국민4044',
                  });
                }
                onSave(item.id, { ...form, extra_cost: 0 });
              } catch (err) { alert('되돌리기 실패'); }
            }} style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '4px', border: '0.5px solid #854F0B', background: '#fff', color: '#854F0B', cursor: 'pointer' }}>되돌리기</button>
          </div>
        )}
        {[
          { label: '판매금액', value: salePrice.toLocaleString()+'원', color: '#1a1a1a', strikethrough: isCancelled },
          { label: '서비스이용료', value: `-${fee.toLocaleString()}원 (${feeRate}%)`, color: '#e53935', strikethrough: isCancelled },
          { label: '정산예정금액', value: settlement.toLocaleString()+'원', color: '#1a1a1a', bold: true, strikethrough: isCancelled },
          { label: '구매가', value: purchasePrice.toLocaleString()+'원', color: '#1a1a1a', strikethrough: isCancelled },
          { label: '배송비', value: shippingCost.toLocaleString()+'원', color: '#1a1a1a' },
          { label: '추가액', value: (extraCost >= 0 ? '' : '') + extraCost.toLocaleString()+'원', color: extraCost < 0 ? '#e53935' : '#1a1a1a' },
        ].map(r => (
          <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '5px' }}>
            <span style={{ color: '#666' }}>{r.label}</span>
            <span style={{ color: r.color, fontWeight: r.bold ? '600' : '400', textDecoration: r.strikethrough ? 'line-through' : 'none', opacity: r.strikethrough ? 0.4 : 1 }}>{r.value}</span>
          </div>
        ))}
        <div style={{ borderTop: '0.5px solid #e0e0e0', margin: '6px 0' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
          <span style={{ color: '#444', fontWeight: '500' }}>마진</span>
          <span style={{ color: margin >= 0 ? '#1D9E75' : '#e53935', fontWeight: '700' }}>{margin >= 0 ? '+' : ''}{margin.toLocaleString()}원</span>
        </div>
        <div style={{ height: '5px', borderRadius: '3px', background: '#e0e0e0', overflow: 'hidden' }}>
          <div style={{ height: '100%', borderRadius: '3px', background: margin >= 0 ? '#1D9E75' : '#e53935', width: `${Math.max(0, Math.min(100, (margin / salePrice) * 100))}%` }} />
        </div>
      </div>
    </div>
  );
}

function OrderCard({ order, orderIndex, totalOrders, currentPage, pageLimit, cards, platforms, accounts, couriers, trackerClientId, isSelected, onSelect, onStatusChange, onPurchaseSave, onRefundSave }) {
  const [status, setStatus] = useState(order.status);
  const [memo, setMemo] = useState(order.memo || '');
  const [memoSavedTime, setMemoSavedTime] = useState('');
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.ready;
  const items = order.items || [];

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    setStatus(newStatus);
    await onStatusChange(order.id, newStatus);

  };

  return (
    <div style={{ background: '#fff', border: order.is_blacklisted ? '2px solid #1a1a1a' : '0.5px solid #e8e8e8', borderLeft: order.is_blacklisted ? '6px solid #1a1a1a' : `4px solid ${BORDER_COLORS[status]}`, borderRadius: '12px', overflow: 'hidden', marginBottom: '12px', minWidth: '1000px', maxWidth: '1000px' }}>
      {order.is_blacklisted && (
        <div style={{ padding: '8px 14px', background: '#1a1a1a', color: '#fff', fontSize: '12px', fontWeight: '500' }}>
          ⚠️ 블랙리스트 고객{order.blacklist_reason ? ` - ${order.blacklist_reason}` : ''}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px', borderBottom: '0.5px solid #e8e8e8' }}>
        <div>
          <div style={{ padding: '12px 14px', background: '#E1F5EE', borderBottom: '0.5px solid #9FE1CB', borderRight: '0.5px solid #9FE1CB' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
              <input type="checkbox" checked={!!isSelected} onChange={onSelect} style={{ width: '15px', height: '15px', accentColor: '#1D9E75', cursor: 'pointer', flexShrink: 0 }} />
              <span style={{ fontSize: '11px', fontWeight: '600', color: '#888', background: '#f0f0f0', padding: '2px 7px', borderRadius: '4px' }}>#{totalOrders - (currentPage-1)*pageLimit - orderIndex}</span>
              <span style={{ fontSize: '12px', fontWeight: '500', color: '#185FA5' }}>{order.order_number}</span>
              <span style={{ fontSize: '11px', color: '#666' }}>{order.ordered_at ? new Date(order.ordered_at).toLocaleString('ko-KR', {timeZone:'Asia/Seoul', year:'numeric', month:'2-digit', day:'2-digit', hour:'2-digit', minute:'2-digit'}) : ''}</span>
              <span style={{ fontSize: '11px', color: '#666' }}>구매자: {order.buyer_name}</span>
              <StatusPill status={status} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', fontSize: '12px', marginBottom: '6px', flexWrap: 'wrap' }}>
              <span><span style={{ color: '#0F6E56', fontWeight: '500' }}>수령인</span> <strong>{order.receiver_name}</strong></span>
              <span><span style={{ color: '#0F6E56', fontWeight: '500' }}>연락처</span> <span style={{ color: '#185FA5', fontWeight: '500' }}>{order.receiver_phone}</span></span>
              <span><span style={{ color: '#0F6E56', fontWeight: '500' }}>우편번호</span> {order.postal_code}</span>
            </div>
            <div style={{ fontSize: '12px', marginBottom: '8px' }}><span style={{ color: '#0F6E56', fontWeight: '500' }}>주소</span> {order.address1} {order.address2}</div>
            {order.delivery_message && <div style={{ fontSize: '11px', color: '#854F0B', background: '#FAEEDA', padding: '3px 8px', borderRadius: '4px', marginBottom: '8px', display: 'inline-block' }}>배송메시지: {order.delivery_message}</div>}
            <div style={{ display: 'flex', gap: '6px' }}>
              <button onClick={() => { navigator.clipboard.writeText(order.receiver_name); alert('수령인 이름이 복사됐습니다'); }} style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '6px', border: '0.5px solid #1D9E75', background: '#E1F5EE', color: '#085041', cursor: 'pointer' }}>수령인 복사</button>
              <button onClick={() => { navigator.clipboard.writeText(`${order.address1} ${order.address2}`); alert('주소가 복사됐습니다'); }} style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '6px', border: '0.5px solid #1D9E75', background: '#E1F5EE', color: '#085041', cursor: 'pointer' }}>주소 복사</button>
              {order.delivery_message && (
                <button onClick={() => { navigator.clipboard.writeText(order.delivery_message); alert('배송메시지가 복사됐습니다'); }} style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '6px', border: '0.5px solid #1D9E75', background: '#E1F5EE', color: '#085041', cursor: 'pointer' }}>배송메시지 복사</button>
              )}
            </div>
          </div>
          <div style={{ padding: '10px 14px', borderRight: '0.5px solid #e8e8e8' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <span style={{ fontSize: '11px', fontWeight: '500', color: '#666' }}>일반관리 메모</span>
              {memoSavedTime && <span style={{ fontSize: '11px', color: '#1D9E75' }}>저장완료 {memoSavedTime}</span>}
            </div>
            <textarea id={`memo-${order.id}`} value={memo} onChange={e => setMemo(e.target.value)} placeholder="날짜 입력 버튼으로 타임스탬프를 찍고 메모를 입력하세요." style={{ width: '100%', fontSize: '12px', padding: '6px 8px', borderRadius: '6px', border: '0.5px solid #ddd', resize: 'vertical', minHeight: '56px', lineHeight: '1.8', boxSizing: 'border-box', marginBottom: '6px' }} />
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <button onClick={() => {
                const now = new Date();
                const p = n => n.toString().padStart(2,'0');
                const ts = now.getFullYear()+'.'+p(now.getMonth()+1)+'.'+p(now.getDate())+' '+p(now.getHours())+':'+p(now.getMinutes())+':'+p(now.getSeconds());
                const ta = document.getElementById('memo-'+order.id);
                const pos = ta ? ta.selectionStart : memo.length;
                const stamp = (memo.length > 0 && memo[memo.length-1] !== '\n' ? '\n' : '') + ts + ' : ';
                const newVal = memo.slice(0, pos) + stamp + memo.slice(pos);
                setMemo(newVal);
                setTimeout(() => { if (ta) { ta.focus(); ta.setSelectionRange(pos+stamp.length, pos+stamp.length); } }, 0);
              }} style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: '0.5px solid #185FA5', background: '#E6F1FB', color: '#185FA5', cursor: 'pointer', whiteSpace: 'nowrap' }}>날짜 입력</button>
              <button onClick={async () => {
                const now = new Date();
                const p = n => n.toString().padStart(2,'0');
                const ts = now.getFullYear()+'.'+p(now.getMonth()+1)+'.'+p(now.getDate())+' '+p(now.getHours())+':'+p(now.getMinutes())+':'+p(now.getSeconds());
                await api.patch('/orders/'+order.id+'/memo', { memo });
                setMemoSavedTime(ts);
              }} style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: 'none', background: '#1D9E75', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' }}>저장</button>
            </div>
          </div>
        </div>
        <div style={{ padding: '12px 14px', background: '#fafafa', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div>
            <div style={{ fontSize: '11px', color: '#888', marginBottom: '3px', fontWeight: '500' }}>판매처</div>
            <div style={{ fontSize: '15px', fontWeight: '700', color: '#e53935' }}>{order.platform}</div>
            {order.sub_platform && <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>{order.sub_platform}</div>}
          </div>
          <div>
            <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px', fontWeight: '500' }}>상태 변경</div>
            <div style={{ position: 'relative' }}>
              <select value={status} onChange={handleStatusChange} style={{ width: '100%', fontSize: '12px', padding: '7px 26px 7px 10px', borderRadius: '8px', border: '0.5px solid #ddd', background: '#fff', appearance: 'none', cursor: 'pointer' }}>
                {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                  <option key={key} value={key} disabled={key !== status && !cfg.allowed.includes(key)}>{val.label}</option>
                ))}
              </select>
              <span style={{ position: 'absolute', right: '9px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', color: '#888', pointerEvents: 'none' }}>▾</span>
            </div>
          </div>
          <div style={{ flex: 1, border: '1px dashed #e0e0e0', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: '10px', color: '#ccc' }}>차후 추가 영역</span>
          </div>
        </div>
      </div>
      {items.map((item, itemIndex) => (
        <div key={item.id}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px', borderBottom: '0.5px solid #e8e8e8', background: '#f9f9f9' }}>
            <div style={{ padding: '10px 14px', borderRight: '0.5px solid #e8e8e8' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {item.product_thumbnail ? (
                  <img src={`https://admin.jinhalife.co.kr${item.product_thumbnail}`} alt="" style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '6px', border: '0.5px solid #e8e8e8', flexShrink: 0 }} />
                ) : (
                  <div style={{ width: '100px', height: '100px', borderRadius: '6px', border: '1px dashed #B5D4F4', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8px', color: '#185FA5' }}>썸네일</div>
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '10px', color: '#aaa', marginBottom: '2px' }}>{item.product_order_number}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                    <div style={{ fontSize: '13px', fontWeight: '600' }}>{item.product_name}</div>
                    {item.seller_product_code && (
                      <button onClick={() => window.open(`/products?search=${encodeURIComponent(item.seller_product_code)}`, '_blank')}
                        title="상품관리에서 보기"
                        style={{ flexShrink: 0, width: '20px', height: '20px', borderRadius: '50%', border: '0.5px solid #e8e8e8', background: '#f9f9f9', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                        <svg width="11" height="11" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="5" cy="5" r="3.5" stroke="#aaa" strokeWidth="1.2"/>
                          <line x1="7.8" y1="7.8" x2="10.5" y2="10.5" stroke="#aaa" strokeWidth="1.2" strokeLinecap="round"/>
                        </svg>
                      </button>
                    )}
                    {(() => {
                      const link = getPlatformProductLink(order.platform, item);
                      if (!link) return null;
                      return (
                        <button
                          onClick={() => link.url ? window.open(link.url, '_blank') : alert(link.message)}
                          title={link.url ? `${order.platform}에서 보기` : link.message}
                          style={{ flexShrink: 0, width: '20px', height: '20px', borderRadius: '50%', border: '0.5px solid #e8e8e8', background: '#f9f9f9', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" stroke="#aaa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <polyline points="15 3 21 3 21 9" stroke="#aaa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <line x1="10" y1="14" x2="21" y2="3" stroke="#aaa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      );
                    })()}
                  </div>
                  {item.exposed_product_name && <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>노출상품명: {item.exposed_product_name}</div>}
                  <div style={{ marginTop: '4px' }}>
                    <LinkEditor item={item} urlField="product_purchase_url" apiField="purchase-url" label="🔗 구매 링크" color="#185FA5" />
                    <LinkEditor item={item} urlField="product_price_compare_url" apiField="price-compare-url" label="📊 가격비교" color="#534AB7" />
                    <PriceEditor item={item} />
                    {order.status === 'claim' && (
                      <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                        <RefundButton item={item} order={order} onRefundSave={onRefundSave} />
                        {!order.is_exchange && (
                          <button onClick={async () => {
                            if (!window.confirm('교환처리로 등록하시겠습니까?\n이후 진행은 메모를 통해 수동으로 관리해주세요.')) return;
                            try {
                              await api.patch('/orders/' + order.id + '/exchange');
                              onRefundSave();
                            } catch(e) { alert('처리 실패'); }
                          }} style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: '0.5px solid #378ADD', background: '#fff', color: '#378ADD', cursor: 'pointer', whiteSpace: 'nowrap' }}>교환처리</button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ width: '0.5px', background: '#e8e8e8', alignSelf: 'stretch', margin: '0 12px', flexShrink: 0 }} />
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexShrink: 0 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '11px', color: '#aaa', marginBottom: '3px' }}>수량</div>
                    <div style={{ fontSize: '18px', fontWeight: '500', color: '#E24B4A' }}>{item.quantity}개</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '11px', color: '#aaa', marginBottom: '3px' }}>총 가격</div>
                    <div style={{ fontSize: '12px', color: '#aaa' }}>{item.sale_price?.toLocaleString()}원</div>
                  </div>
                </div>
              </div>
            </div>
            <div style={{ padding: '10px 14px', background: '#f5f5f5' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#666', marginBottom: '3px' }}><span>배송비{itemIndex === 0 && order.shipping_fee > 0 ? <span style={{ fontSize: '10px', color: '#aaa', marginLeft: '4px' }}>* 배송비는 주문 전체 기준입니다</span> : ''}</span><span style={{ fontWeight: '500', color: '#1a1a1a' }}>{itemIndex === 0 ? (order.shipping_fee?.toLocaleString() || 0) : 0}원</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#666' }}><span>합계 (+배송비)</span><span style={{ fontWeight: '500', color: '#1a1a1a' }}>{((item.sale_price || 0) + (itemIndex === 0 ? (order.shipping_fee || 0) : 0)).toLocaleString()}원</span></div>
            </div>
          </div>
          <PurchaseSection item={item} order={order} itemIndex={itemIndex} platforms={platforms} accounts={accounts} cards={cards} couriers={couriers} trackerClientId={trackerClientId} onSave={onPurchaseSave} />
        </div>
      ))}
    </div>
  );
}




function OrderFetchButtons({ onFetchComplete }) {
  const [fetching, setFetching] = useState(false);
  const [result, setResult] = useState(null);

  const handleFetch = async () => {
    if (!window.confirm('스마트스토어 신규 주문을 가져오시겠습니까?\n발주 전 상태의 주문을 자동으로 가져오고 발주확인 처리합니다.')) return;
    setFetching(true);
    try {
      const res = await api.post('/orders/naver-fetch-orders');
      setResult(res.data);
      onFetchComplete();
    } catch (err) {
      alert('주문 가져오기 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setFetching(false);
    }
  };

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '10px 16px', background: '#f9f9f9', border: '0.5px solid #e8e8e8', borderRadius: '10px', marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button onClick={handleFetch} disabled={fetching} style={{ fontSize: '12px', padding: '6px 16px', borderRadius: '8px', border: '0.5px solid #1D9E75', background: fetching ? '#f0f0f0' : '#E1F5EE', color: fetching ? '#aaa' : '#085041', cursor: fetching ? 'not-allowed' : 'pointer', fontWeight: '500', whiteSpace: 'nowrap' }}>
            {fetching ? '가져오는 중...' : '🛒 스마트스토어 주문 가져오기'}
          </button>
          <ElevenFetchButton onFetchComplete={onFetchComplete} />
          <EsmFetchButton onFetchComplete={onFetchComplete} />
          <SellerhubFetchButton onFetchComplete={onFetchComplete} />
          <CoupangFetchButton onFetchComplete={onFetchComplete} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <TossFetchButton onFetchComplete={onFetchComplete} />
        </div>
        <span style={{ fontSize: '11px', color: '#888' }}>발주 전 신규 주문을 자동으로 가져오고 발주확인 처리합니다</span>
      </div>

      {result && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '480px', maxHeight: '70vh', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 24px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '16px', color: '#1a1a1a' }}>스마트스토어 주문 가져오기 결과</div>
            <div style={{ flex: 1, overflowY: 'auto', marginBottom: '16px', border: '0.5px solid #e8e8e8', borderRadius: '8px' }}>
              {(result.newOrders || []).map(orderNum => (
                <div key={orderNum} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderBottom: '0.5px solid #f0f0f0' }}>
                  <span style={{ fontSize: '12px', color: '#1D9E75', fontWeight: '500', flexShrink: 0 }}>✓ 신규</span>
                  <span style={{ fontSize: '12px', color: '#1a1a1a' }}>{orderNum}</span>
                </div>
              ))}
              {(result.duplicates || []).map(orderNum => (
                <div key={orderNum} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderBottom: '0.5px solid #f0f0f0', background: '#fffbf0' }}>
                  <span style={{ fontSize: '12px', color: '#EF9F27', fontWeight: '500', flexShrink: 0 }}>⚠ 중복</span>
                  <span style={{ fontSize: '12px', color: '#1a1a1a' }}>{orderNum}</span>
                  <span style={{ fontSize: '11px', color: '#aaa' }}>(건너뜀)</span>
                </div>
              ))}
              {(result.confirmFailOrders || []).map(orderNum => (
                <div key={orderNum} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderBottom: '0.5px solid #f0f0f0', background: '#fff5f5' }}>
                  <span style={{ fontSize: '12px', color: '#E24B4A', fontWeight: '500', flexShrink: 0 }}>✗ 발주확인 실패</span>
                  <span style={{ fontSize: '12px', color: '#1a1a1a' }}>{orderNum}</span>
                </div>
              ))}
              {(result.newOrders || []).length === 0 && (result.duplicates || []).length === 0 && (
                <div style={{ padding: '16px', color: '#aaa', fontSize: '13px', textAlign: 'center' }}>가져올 신규 주문이 없습니다</div>
              )}
            </div>
            <div style={{ fontSize: '13px', color: '#444', marginBottom: '16px', padding: '10px 14px', background: '#f5f5f5', borderRadius: '8px' }}>
              {result.message}
              {(result.confirmFailOrders || []).length > 0 && (
                <div style={{ color: '#E24B4A', marginTop: '6px', fontSize: '12px' }}>발주확인 실패 {result.confirmFailOrders.length}건은 스마트스토어에서 수동으로 처리해주세요.</div>
              )}
            </div>
            <button onClick={() => setResult(null)} style={{ fontSize: '13px', padding: '10px', borderRadius: '8px', border: 'none', background: '#1D9E75', color: '#fff', cursor: 'pointer', fontWeight: '500' }}>확인</button>
          </div>
        </div>
      )}
    </>
  );
}

function ElevenFetchButton({ onFetchComplete }) {
  const [fetching, setFetching] = useState(false);
  const [result, setResult] = useState(null);

  const handleFetch = async () => {
    if (!window.confirm('11번가 신규 주문을 가져오시겠습니까?\n발주 전 상태의 주문을 자동으로 가져오고 발주확인 처리합니다.')) return;
    setFetching(true);
    try {
      const res = await api.post('/orders/eleven-fetch-orders');
      setResult(res.data);
      onFetchComplete();
    } catch (err) {
      alert('주문 가져오기 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setFetching(false);
    }
  };

  return (
    <>
      <button onClick={handleFetch} disabled={fetching} style={{ fontSize: '12px', padding: '6px 16px', borderRadius: '8px', border: '0.5px solid #E24B4A', background: fetching ? '#f0f0f0' : '#FCEBEB', color: fetching ? '#aaa' : '#A32D2D', cursor: fetching ? 'not-allowed' : 'pointer', fontWeight: '500', whiteSpace: 'nowrap' }}>
        {fetching ? '가져오는 중...' : '🛒 11번가 주문 가져오기'}
      </button>

      {result && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '480px', maxHeight: '70vh', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 24px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '16px', color: '#1a1a1a' }}>11번가 주문 가져오기 결과</div>
            <div style={{ flex: 1, overflowY: 'auto', marginBottom: '16px', border: '0.5px solid #e8e8e8', borderRadius: '8px' }}>
              {(result.newOrders || []).map(orderNum => (
                <div key={orderNum} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderBottom: '0.5px solid #f0f0f0' }}>
                  <span style={{ fontSize: '12px', color: '#1D9E75', fontWeight: '500', flexShrink: 0 }}>✓ 신규</span>
                  <span style={{ fontSize: '12px', color: '#1a1a1a' }}>{orderNum}</span>
                </div>
              ))}
              {(result.duplicates || []).map(orderNum => (
                <div key={orderNum} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderBottom: '0.5px solid #f0f0f0', background: '#fffbf0' }}>
                  <span style={{ fontSize: '12px', color: '#EF9F27', fontWeight: '500', flexShrink: 0 }}>⚠ 중복</span>
                  <span style={{ fontSize: '12px', color: '#1a1a1a' }}>{orderNum}</span>
                  <span style={{ fontSize: '11px', color: '#aaa' }}>(건너뜀)</span>
                </div>
              ))}
              {(result.confirmFailOrders || []).map(orderNum => (
                <div key={orderNum} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderBottom: '0.5px solid #f0f0f0', background: '#fff5f5' }}>
                  <span style={{ fontSize: '12px', color: '#E24B4A', fontWeight: '500', flexShrink: 0 }}>✗ 발주확인 실패</span>
                  <span style={{ fontSize: '12px', color: '#1a1a1a' }}>{orderNum}</span>
                </div>
              ))}
              {(result.newOrders || []).length === 0 && (result.duplicates || []).length === 0 && (
                <div style={{ padding: '16px', color: '#aaa', fontSize: '13px', textAlign: 'center' }}>가져올 신규 주문이 없습니다</div>
              )}
            </div>
            <div style={{ fontSize: '13px', color: '#444', marginBottom: '16px', padding: '10px 14px', background: '#f5f5f5', borderRadius: '8px' }}>
              {result.message}
              {(result.confirmFailOrders || []).length > 0 && (
                <div style={{ color: '#E24B4A', marginTop: '6px', fontSize: '12px' }}>발주확인 실패 {result.confirmFailOrders.length}건은 11번가에서 수동으로 처리해주세요.</div>
              )}
            </div>
            <button onClick={() => setResult(null)} style={{ fontSize: '13px', padding: '10px', borderRadius: '8px', border: 'none', background: '#1D9E75', color: '#fff', cursor: 'pointer', fontWeight: '500' }}>확인</button>
          </div>
        </div>
      )}
    </>
  );
}

function EsmFetchButton({ onFetchComplete }) {
  const [fetching, setFetching] = useState(false);
  const [result, setResult] = useState(null);
  const handleFetch = async () => {
    if (!window.confirm('ESM(지마켓/옥션) 신규 주문을 가져오시겠습니까?')) return;
    setFetching(true);
    try {
      const res = await api.post('/orders/esm-fetch-orders');
      setResult(res.data);
      onFetchComplete();
    } catch (err) {
      alert('주문 가져오기 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setFetching(false);
    }
  };
  return (
    <>
      <button onClick={handleFetch} disabled={fetching} style={{ fontSize: '12px', padding: '6px 16px', borderRadius: '8px', border: '0.5px solid #185FA5', background: fetching ? '#f0f0f0' : '#E6F1FB', color: fetching ? '#aaa' : '#185FA5', cursor: fetching ? 'not-allowed' : 'pointer', fontWeight: '500', whiteSpace: 'nowrap' }}>
        {fetching ? '가져오는 중...' : '🛒 ESM 주문 가져오기'}
      </button>
      {result && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '480px', maxHeight: '70vh', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 24px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '16px', color: '#1a1a1a' }}>ESM 주문 가져오기 결과</div>
            <div style={{ flex: 1, overflowY: 'auto', marginBottom: '16px', border: '0.5px solid #e8e8e8', borderRadius: '8px' }}>
              {(result.newOrders || []).map(orderNum => (
                <div key={orderNum} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderBottom: '0.5px solid #f0f0f0' }}>
                  <span style={{ fontSize: '12px', color: '#1D9E75', fontWeight: '500', flexShrink: 0 }}>✓ 신규</span>
                  <span style={{ fontSize: '12px', color: '#1a1a1a' }}>{orderNum}</span>
                </div>
              ))}
              {(result.duplicates || []).map(orderNum => (
                <div key={orderNum} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderBottom: '0.5px solid #f0f0f0', background: '#fffbf0' }}>
                  <span style={{ fontSize: '12px', color: '#EF9F27', fontWeight: '500', flexShrink: 0 }}>⚠ 중복</span>
                  <span style={{ fontSize: '12px', color: '#1a1a1a' }}>{orderNum}</span>
                  <span style={{ fontSize: '11px', color: '#aaa' }}>(건너뜀)</span>
                </div>
              ))}
              {(result.newOrders || []).length === 0 && (result.duplicates || []).length === 0 && (
                <div style={{ padding: '16px', color: '#aaa', fontSize: '13px', textAlign: 'center' }}>가져올 신규 주문이 없습니다</div>
              )}
            </div>
            <div style={{ fontSize: '13px', color: '#444', marginBottom: '16px', padding: '10px 14px', background: '#f5f5f5', borderRadius: '8px' }}>
              {result.message}
            </div>
            <button onClick={() => setResult(null)} style={{ fontSize: '13px', padding: '10px', borderRadius: '8px', border: 'none', background: '#185FA5', color: '#fff', cursor: 'pointer', fontWeight: '500' }}>확인</button>
          </div>
        </div>
      )}
    </>
  );
}
function CoupangFetchButton({ onFetchComplete }) {
  const [fetching, setFetching] = useState(false);
  const [result, setResult] = useState(null);
  const handleFetch = async () => {
    if (!window.confirm('쿠팡 신규 주문을 가져오시겠습니까?')) return;
    setFetching(true);
    try {
      const res = await api.post('/orders/coupang-fetch-orders');
      setResult(res.data);
      onFetchComplete();
    } catch (err) {
      alert('주문 가져오기 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setFetching(false);
    }
  };
  return (
    <>
      <button onClick={handleFetch} disabled={fetching} style={{ fontSize: '12px', padding: '6px 16px', borderRadius: '8px', border: '0.5px solid #E24B4A', background: fetching ? '#f0f0f0' : '#FCEBEB', color: fetching ? '#aaa' : '#A32D2D', cursor: fetching ? 'not-allowed' : 'pointer', fontWeight: '500', whiteSpace: 'nowrap' }}>
        {fetching ? '가져오는 중...' : '🛒 쿠팡 주문 가져오기'}
      </button>
      {result && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '480px', maxHeight: '70vh', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 24px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '16px', color: '#1a1a1a' }}>쿠팡 주문 가져오기 결과</div>
            <div style={{ flex: 1, overflowY: 'auto', marginBottom: '16px', border: '0.5px solid #e8e8e8', borderRadius: '8px' }}>
              {(result.newOrders || []).map(orderNum => (
                <div key={orderNum} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderBottom: '0.5px solid #f0f0f0' }}>
                  <span style={{ fontSize: '12px', color: '#1D9E75', fontWeight: '500', flexShrink: 0 }}>✓ 신규</span>
                  <span style={{ fontSize: '12px', color: '#1a1a1a' }}>{orderNum}</span>
                </div>
              ))}
              {(result.duplicates || []).map(orderNum => (
                <div key={orderNum} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderBottom: '0.5px solid #f0f0f0', background: '#fffbf0' }}>
                  <span style={{ fontSize: '12px', color: '#EF9F27', fontWeight: '500', flexShrink: 0 }}>⚠ 중복</span>
                  <span style={{ fontSize: '12px', color: '#1a1a1a' }}>{orderNum}</span>
                  <span style={{ fontSize: '11px', color: '#aaa' }}>(건너뜀)</span>
                </div>
              ))}
              {(result.confirmFailOrders || []).length > 0 && (
                <div style={{ padding: '8px 14px', fontSize: '12px', color: '#A32D2D' }}>
                  ⚠ 상품준비중 처리 실패: {result.confirmFailOrders.join(', ')}
                </div>
              )}
              {(result.newOrders || []).length === 0 && (result.duplicates || []).length === 0 && (
                <div style={{ padding: '16px', color: '#aaa', fontSize: '13px', textAlign: 'center' }}>가져올 신규 주문이 없습니다</div>
              )}
            </div>
            <div style={{ fontSize: '13px', color: '#444', marginBottom: '16px', padding: '10px 14px', background: '#f5f5f5', borderRadius: '8px' }}>
              {result.message}
            </div>
            <button onClick={() => setResult(null)} style={{ fontSize: '13px', padding: '10px', borderRadius: '8px', border: 'none', background: '#3C3489', color: '#fff', cursor: 'pointer', fontWeight: '500' }}>확인</button>
          </div>
        </div>
      )}
    </>
  );
}
// [기능 추가 - 2026-09-10] 토스쇼핑 신규 주문 가져오기 버튼. 쿠팡과 정확히 같은 패턴.
function TossFetchButton({ onFetchComplete }) {
  const [fetching, setFetching] = useState(false);
  const [result, setResult] = useState(null);
  const handleFetch = async () => {
    if (!window.confirm('토스쇼핑 신규 주문을 가져오시겠습니까?')) return;
    setFetching(true);
    try {
      const res = await api.post('/orders/toss-fetch-orders');
      setResult(res.data);
      onFetchComplete();
    } catch (err) {
      alert('주문 가져오기 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setFetching(false);
    }
  };
  return (
    <>
      <button onClick={handleFetch} disabled={fetching} style={{ fontSize: '12px', padding: '6px 16px', borderRadius: '8px', border: '0.5px solid #0064FF', background: fetching ? '#f0f0f0' : '#E6EEFF', color: fetching ? '#aaa' : '#0040B0', cursor: fetching ? 'not-allowed' : 'pointer', fontWeight: '500', whiteSpace: 'nowrap' }}>
        {fetching ? '가져오는 중...' : '🛒 토스쇼핑 주문 가져오기'}
      </button>
      {result && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '480px', maxHeight: '70vh', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 24px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '16px', color: '#1a1a1a' }}>토스쇼핑 주문 가져오기 결과</div>
            <div style={{ flex: 1, overflowY: 'auto', marginBottom: '16px', border: '0.5px solid #e8e8e8', borderRadius: '8px' }}>
              {(result.newOrders || []).map(orderNum => (
                <div key={orderNum} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderBottom: '0.5px solid #f0f0f0' }}>
                  <span style={{ fontSize: '12px', color: '#1D9E75', fontWeight: '500', flexShrink: 0 }}>✓ 신규</span>
                  <span style={{ fontSize: '12px', color: '#1a1a1a' }}>{orderNum}</span>
                </div>
              ))}
              {(result.duplicates || []).map(orderNum => (
                <div key={orderNum} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderBottom: '0.5px solid #f0f0f0', background: '#fffbf0' }}>
                  <span style={{ fontSize: '12px', color: '#EF9F27', fontWeight: '500', flexShrink: 0 }}>⚠ 중복</span>
                  <span style={{ fontSize: '12px', color: '#1a1a1a' }}>{orderNum}</span>
                  <span style={{ fontSize: '11px', color: '#aaa' }}>(건너뜀)</span>
                </div>
              ))}
              {(result.confirmFailOrders || []).length > 0 && (
                <div style={{ padding: '8px 14px', fontSize: '12px', color: '#A32D2D' }}>
                  ⚠ 상품준비중 처리 실패: {result.confirmFailOrders.join(', ')}
                </div>
              )}
              {(result.newOrders || []).length === 0 && (result.duplicates || []).length === 0 && (
                <div style={{ padding: '16px', color: '#aaa', fontSize: '13px', textAlign: 'center' }}>가져올 신규 주문이 없습니다</div>
              )}
            </div>
            <div style={{ fontSize: '13px', color: '#444', marginBottom: '16px', padding: '10px 14px', background: '#f5f5f5', borderRadius: '8px' }}>
              {result.message}
            </div>
            <button onClick={() => setResult(null)} style={{ fontSize: '13px', padding: '10px', borderRadius: '8px', border: 'none', background: '#3C3489', color: '#fff', cursor: 'pointer', fontWeight: '500' }}>확인</button>
          </div>
        </div>
      )}
    </>
  );
}
function SellerhubFetchButton({ onFetchComplete }) {
  const [fetching, setFetching] = useState(false);
  const [result, setResult] = useState(null);
  const handleFetch = async () => {
    if (!window.confirm('셀러허브 신규 주문을 가져오시겠습니까?')) return;
    setFetching(true);
    try {
      const res = await api.post('/orders/sellerhub-fetch-orders');
      setResult(res.data);
      onFetchComplete();
    } catch (err) {
      alert('주문 가져오기 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setFetching(false);
    }
  };
  return (
    <>
      <button onClick={handleFetch} disabled={fetching} style={{ fontSize: '12px', padding: '6px 16px', borderRadius: '8px', border: '0.5px solid #7F77DD', background: fetching ? '#f0f0f0' : '#EEEDFE', color: fetching ? '#aaa' : '#3C3489', cursor: fetching ? 'not-allowed' : 'pointer', fontWeight: '500', whiteSpace: 'nowrap' }}>
        {fetching ? '가져오는 중...' : '🛒 셀러허브 주문 가져오기'}
      </button>
      {result && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '480px', maxHeight: '70vh', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 24px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '16px', color: '#1a1a1a' }}>셀러허브 주문 가져오기 결과</div>
            <div style={{ flex: 1, overflowY: 'auto', marginBottom: '16px', border: '0.5px solid #e8e8e8', borderRadius: '8px' }}>
              {(result.newOrders || []).map(orderNum => (
                <div key={orderNum} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderBottom: '0.5px solid #f0f0f0' }}>
                  <span style={{ fontSize: '12px', color: '#1D9E75', fontWeight: '500', flexShrink: 0 }}>✓ 신규</span>
                  <span style={{ fontSize: '12px', color: '#1a1a1a' }}>{orderNum}</span>
                </div>
              ))}
              {(result.duplicates || []).map(orderNum => (
                <div key={orderNum} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderBottom: '0.5px solid #f0f0f0', background: '#fffbf0' }}>
                  <span style={{ fontSize: '12px', color: '#EF9F27', fontWeight: '500', flexShrink: 0 }}>⚠ 중복</span>
                  <span style={{ fontSize: '12px', color: '#1a1a1a' }}>{orderNum}</span>
                  <span style={{ fontSize: '11px', color: '#aaa' }}>(건너뜀)</span>
                </div>
              ))}
              {(result.newOrders || []).length === 0 && (result.duplicates || []).length === 0 && (
                <div style={{ padding: '16px', color: '#aaa', fontSize: '13px', textAlign: 'center' }}>가져올 신규 주문이 없습니다</div>
              )}
            </div>
            <div style={{ fontSize: '13px', color: '#444', marginBottom: '16px', padding: '10px 14px', background: '#f5f5f5', borderRadius: '8px' }}>
              {result.message}
            </div>
            <button onClick={() => setResult(null)} style={{ fontSize: '13px', padding: '10px', borderRadius: '8px', border: 'none', background: '#3C3489', color: '#fff', cursor: 'pointer', fontWeight: '500' }}>확인</button>
          </div>
        </div>
      )}
    </>
  );
}
const PLATFORMS = ['스마트스토어', '11번가', '쿠팡', 'ESM', '셀러허브'];
const UNSUPPORTED_PLATFORMS = [];

function InvoiceSendPanel({ pendingCounts, onSendComplete }) {
  const [showModal, setShowModal] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState([]);
  const [done, setDone] = useState(false);

  const handlePlatformClick = (platform) => {
    if (UNSUPPORTED_PLATFORMS.includes(platform)) {
      alert(`${platform}은 현재 미구현입니다. 수동으로 처리해주세요.`);
      return;
    }
    setSelectedPlatform(platform);
    setResults([]);
    setDone(false);
    setShowModal(true);
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await api.post('/orders/invoice-send-bulk', { platform: selectedPlatform });
      if (res.data.results.length === 0) {
        alert('전송할 건이 없습니다.');
        setShowModal(false);
      } else {
        setResults(res.data.results);
        setDone(true);
        onSendComplete();
      }
    } catch (err) {
      alert('전송 중 오류가 발생했습니다.');
    } finally {
      setSending(false);
    }
  };

  const handleResend = async (orderItemId) => {
    try {
      const res = await api.post('/orders/invoice-send-single', { order_item_id: orderItemId, platform: selectedPlatform });
      if (res.data.already_sent_by_same_delivery) {
        if (window.confirm(res.data.message)) {
          await api.post('/orders/invoice-send-single', { order_item_id: orderItemId, platform: selectedPlatform, force_complete: true });
          setResults(prev => prev.map(r => r.order_item_id === orderItemId ? { ...r, success: true, message: '완료 처리됐습니다' } : r));
          onSendComplete();
        }
      } else {
        setResults(prev => prev.map(r => r.order_item_id === orderItemId ? { ...r, success: true, message: '전송 완료' } : r));
        onSendComplete();
      }
    } catch (err) {
      const errData = err.response?.data;
      if (errData?.already_processed) {
        if (window.confirm(errData.message)) {
          await api.post('/orders/invoice-send-single', { order_item_id: orderItemId, platform: selectedPlatform, force_complete: true });
          setResults(prev => prev.map(r => r.order_item_id === orderItemId ? { ...r, success: true, message: '완료 처리됐습니다' } : r));
          onSendComplete();
        }
      } else {
        setResults(prev => prev.map(r => r.order_item_id === orderItemId ? { ...r, success: false, message: errData?.message || err.message } : r));
      }
    }
  };

  const successCount = results.filter(r => r.success).length;
  const failCount = results.filter(r => !r.success).length;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', background: '#f9f9f9', border: '0.5px solid #e8e8e8', borderRadius: '10px', marginTop: '8px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '12px', color: '#666', fontWeight: '500', marginRight: '4px' }}>판매처 송장 전송</span>
        {PLATFORMS.map(platform => {
          const count = platform === 'ESM' ? (pendingCounts['지마켓'] || 0) + (pendingCounts['옥션'] || 0) : (pendingCounts[platform] || 0);
          const unsupported = UNSUPPORTED_PLATFORMS.includes(platform);
          return (
            <button key={platform} onClick={() => handlePlatformClick(platform)}
              style={{
                fontSize: '12px', padding: '6px 14px', borderRadius: '8px', cursor: 'pointer',
                border: `0.5px solid ${unsupported ? '#ddd' : '#185FA5'}`,
                background: unsupported ? '#f5f5f5' : '#E6F1FB',
                color: unsupported ? '#aaa' : '#185FA5',
                display: 'flex', alignItems: 'center', gap: '5px'
              }}>
              {platform}
              <span style={{
                fontSize: '11px', padding: '1px 6px', borderRadius: '10px',
                background: unsupported ? '#e0e0e0' : count > 0 ? '#185FA5' : '#b0c4d8',
                color: '#fff', fontWeight: '500'
              }}>{unsupported ? '-' : count}</span>
            </button>
          );
        })}
      </div>

      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => e.target === e.currentTarget && !sending && setShowModal(false)}>
          <div style={{ background: '#fff', borderRadius: '14px', width: '560px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ padding: '16px 20px 14px', borderBottom: '0.5px solid #e8e8e8', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: '#fff', borderRadius: '14px 14px 0 0' }}>
              <div style={{ fontSize: '14px', fontWeight: '500', color: '#185FA5' }}>
                {selectedPlatform} 송장 일괄 전송
              </div>
              {!sending && <button onClick={() => setShowModal(false)} style={{ fontSize: '18px', color: '#aaa', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>}
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
              {!done ? (
                <div style={{ textAlign: 'center', padding: '30px 0' }}>
                  <div style={{ fontSize: '13px', color: '#444', marginBottom: '8px' }}>
                    {selectedPlatform}의 미전송 건을 일괄 전송합니다.
                  </div>
                  <div style={{ fontSize: '12px', color: '#888', marginBottom: '20px' }}>
                    미전송 건수: <strong style={{ color: '#185FA5' }}>{selectedPlatform === 'ESM' ? (pendingCounts['지마켓'] || 0) + (pendingCounts['옥션'] || 0) : (pendingCounts[selectedPlatform] || 0)}건</strong>
                  </div>
                  {sending ? (
                    <div style={{ fontSize: '13px', color: '#185FA5' }}>전송 중...</div>
                  ) : (
                    <button onClick={handleSend} style={{ fontSize: '13px', padding: '10px 30px', borderRadius: '8px', border: 'none', background: '#185FA5', color: '#fff', cursor: 'pointer', fontWeight: '500' }}>
                      전송 시작
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
                    <span style={{ fontSize: '12px', padding: '4px 12px', borderRadius: '6px', background: '#E1F5EE', color: '#0F6E56', fontWeight: '500' }}>완료 {successCount}건</span>
                    <span style={{ fontSize: '12px', padding: '4px 12px', borderRadius: '6px', background: '#FCEBEB', color: '#A32D2D', fontWeight: '500' }}>실패 {failCount}건</span>
                  </div>
                  {results.map((r, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderBottom: '0.5px solid #f0f0f0', borderRadius: '6px', background: r.success ? '#f9fff9' : '#fff9f9', marginBottom: '4px' }}>
                      <span style={{ fontSize: '11px', fontWeight: '500', color: r.success ? '#0F6E56' : '#A32D2D', flexShrink: 0 }}>{r.success ? '✓ 완료' : '✗ 실패'}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '12px', color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.product_name}</div>
                        <div style={{ fontSize: '11px', color: '#888' }}>송장: {r.tracking_number} {!r.success && <span style={{ color: '#A32D2D' }}>· {r.message}</span>}</div>
                      </div>
                        {!r.success && (
                          <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                            {!r.manual_required && <button onClick={() => handleResend(r.order_item_id)} style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: '0.5px solid #185FA5', background: '#E6F1FB', color: '#185FA5', cursor: 'pointer', whiteSpace: 'nowrap' }}>재전송</button>}
                            <button onClick={async () => {
                              if (!window.confirm('수동으로 처리 완료하시겠습니까?\n(직접 처리한 경우 사용)')) return;
                              await api.post('/orders/invoice-send-single', { order_item_id: r.order_item_id, platform: selectedPlatform, force_complete: true });
                              setResults(prev => prev.map(x => x.order_item_id === r.order_item_id ? { ...x, success: true, message: '수동처리 완료' } : x));
                              onSendComplete();
                            }} style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: '0.5px solid #888', background: '#f5f5f5', color: '#666', cursor: 'pointer', whiteSpace: 'nowrap' }}>수동처리 완료</button>
                          </div>
                        )}
                    </div>
                  ))}
                </>
              )}
            </div>

            {done && (
              <div style={{ padding: '12px 20px', borderTop: '0.5px solid #e8e8e8' }}>
                <button onClick={() => setShowModal(false)} style={{ width: '100%', fontSize: '13px', padding: '10px', borderRadius: '8px', border: 'none', background: '#1D9E75', color: '#fff', cursor: 'pointer', fontWeight: '500' }}>닫기</button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function DeliveryTrackButton() {
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [expiry, setExpiry] = useState(null);
  const [daysLeft, setDaysLeft] = useState(null);

  const fetchExpiry = () => {
    api.get('/orders/tracker-expiry')
      .then(res => { setExpiry(res.data.expiry); setDaysLeft(res.data.daysLeft); })
      .catch(() => {});
  };

  useEffect(() => {
    api.get('/orders/delivery-track/last-run')
      .then(res => setLastRun(res.data.lastRun))
      .catch(() => {});
    fetchExpiry();
  }, []);

  const handleRun = async () => {
    if (!window.confirm('배송추적을 지금 실행하시겠습니까?')) return;
    setRunning(true);
    try {
      const res = await api.post('/orders/delivery-track');
      setLastRun(res.data.executedAt);
      alert('배송추적 실행 완료');
    } catch (err) {
      alert('배송추적 실행 실패');
    } finally {
      setRunning(false);
    }
  };

  const handleRenew = async () => {
    if (!window.confirm('tracker.delivery API 키를 재발급 받으셨나요?\n오늘부터 21일 후로 만료일이 갱신됩니다.')) return;
    try {
      const res = await api.post('/orders/tracker-expiry/renew');
      setExpiry(res.data.expiry);
      setDaysLeft(21);
      alert('만료일이 갱신됐습니다.');
    } catch (err) {
      alert('갱신 실패');
    }
  };

  const expiryWarning = daysLeft !== null && daysLeft <= 3;

  return (
    <>
      {expiryWarning && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', background: '#FAEEDA', border: '0.5px solid #EF9F27', borderRadius: '10px', marginBottom: '8px' }}>
          <span style={{ fontSize: '13px' }}>⚠️</span>
          <span style={{ fontSize: '12px', color: '#854F0B', fontWeight: '500' }}>
            tracker.delivery API 키가 {daysLeft <= 0 ? '만료됐습니다!' : `${daysLeft}일 후 만료됩니다.`} 재발급 후 아래 배송 API 갱신 버튼을 눌러주세요.
          </span>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', background: '#f9f9f9', border: '0.5px solid #e8e8e8', borderRadius: '10px', marginTop: '8px' }}>
        <button onClick={handleRun} disabled={running} style={{ fontSize: '12px', padding: '6px 16px', borderRadius: '8px', border: '0.5px solid #185FA5', background: running ? '#f0f0f0' : '#E6F1FB', color: running ? '#aaa' : '#185FA5', cursor: running ? 'not-allowed' : 'pointer', fontWeight: '500', whiteSpace: 'nowrap' }}>
          {running ? '실행 중...' : '🚚 배송추적 실행 (수동)'}
        </button>
        <span style={{ fontSize: '11px', color: '#888' }}>
          자동 API 마지막 실행: {lastRun || '기록 없음'}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button onClick={handleRenew} style={{ fontSize: '12px', padding: '6px 16px', borderRadius: '8px', border: `0.5px solid ${expiryWarning ? '#EF9F27' : '#ddd'}`, background: expiryWarning ? '#FAEEDA' : '#fff', color: expiryWarning ? '#854F0B' : '#666', cursor: 'pointer', fontWeight: expiryWarning ? '500' : '400', whiteSpace: 'nowrap' }}>
            🔑 배송 API 갱신
          </button>
          <span style={{ fontSize: '11px', color: expiryWarning ? '#854F0B' : '#aaa' }}>
            {expiry ? `만료일: ${expiry} (D-${daysLeft <= 0 ? 'day' : daysLeft})` : '만료일 미설정'}
          </span>
        </div>
      </div>
    </>
  );
}

export default function Orders() {
  const location = useLocation();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [cards, setCards] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [statusCounts, setStatusCounts] = useState({ ready:0, buy:0, ship:0, done:0, claim:0, refund_done:0, cancel:0 });
  const [total, setTotal] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(10);
  const [statusFilter, setStatusFilter] = useState(() => new URLSearchParams(window.location.search).get('status') || '');
  const [platformFilter, setPlatformFilter] = useState(() => new URLSearchParams(window.location.search).get('platform') || '');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const s = params.get('status') || '';
    const p = params.get('platform') || '';
    setStatusFilter(s);
    setPlatformFilter(p);
    setCurrentPage(1);
  }, [location.search]);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [datePreset, setDatePreset] = useState('');
  const [trackerClientId, setTrackerClientId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchType, setSearchType] = useState('order_number');
  const [purchasePlatformFilter, setPurchasePlatformFilter] = useState('');
  const [purchasePlatforms, setPurchasePlatforms] = useState([]);
  const [invoicePendingCounts, setInvoicePendingCounts] = useState({});

  const fetchInvoicePendingCounts = useCallback(async () => {
    try {
      const res = await api.get('/orders/invoice-pending-counts');
      setInvoicePendingCounts(res.data);
    } catch (err) { console.error(err); }
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      const params = { page: currentPage, limit: pageLimit };
      if (statusFilter) params.status = statusFilter;
      if (platformFilter) params.platform = platformFilter;
      if (search) { params.search = search; params.searchType = searchType; }
      if (purchasePlatformFilter) params.purchasePlatform = purchasePlatformFilter;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const res = await api.get('/orders', { params });
      setOrders(res.data.orders);
      setTotal(res.data.total);
      setTotalPages(res.data.totalPages);
    } catch (err) { console.error(err); }
  }, [currentPage, statusFilter, platformFilter, search, searchType, dateFrom, dateTo, pageLimit, purchasePlatformFilter]);

  const fetchCounts = useCallback(async () => {
    try {
      const params = {};
      if (platformFilter) params.platform = platformFilter;
      if (search) { params.search = search; params.searchType = searchType; }
      if (purchasePlatformFilter) params.purchasePlatform = purchasePlatformFilter;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const res = await api.get('/orders/counts', { params });
      setStatusCounts(res.data);
      setTotalCount(Object.values(res.data).reduce((a, b) => a + b, 0));
    } catch (err) { console.error(err); }
  }, [platformFilter, search, searchType, purchasePlatformFilter, dateFrom, dateTo]);

  const fetchSettings = useCallback(async () => {
    try {
      const [c, p, a, cr, tr] = await Promise.all([api.get('/cards'), api.get('/purchase-platforms'), api.get('/purchase-accounts'), api.get('/couriers'), api.get('/orders/tracker-credentials')]);
      setCards(c.data); setPlatforms(p.data); setAccounts(a.data); setCouriers(cr.data); setPurchasePlatforms(p.data);
      setTrackerClientId(tr.data.client_id || '');
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (platformFilter) params.set('platform', platformFilter);
    const newUrl = params.toString() ? `?${params.toString()}` : '/orders';
    navigate(newUrl, { replace: true });
  }, [statusFilter, platformFilter, navigate]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);
  useEffect(() => { fetchCounts(); }, [fetchCounts]);
  useEffect(() => { fetchSettings(); }, [fetchSettings]);
  useEffect(() => { fetchInvoicePendingCounts(); }, [fetchInvoicePendingCounts]);

  const handleStatusChange = async (orderId, status) => {
    await api.patch(`/orders/${orderId}/status`, { status });
    fetchCounts();
  };
  const handlePurchaseSave = async (itemId, data) => { await api.post(`/purchases/${itemId}`, data); };
  const applyDatePreset = (preset) => {
    const now = new Date();
    // 한국시간 기준 오늘 날짜
    const kst = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));
    const today = new Date(kst.getFullYear(), kst.getMonth(), kst.getDate());
    const fmt = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth()+1).padStart(2,'0');
      const day = String(d.getDate()).padStart(2,'0');
      return `${y}-${m}-${day}`;
    };
    setDatePreset(preset);
    setCurrentPage(1);
    if (preset === 'today') {
      setDateFrom(fmt(today));
      setDateTo(fmt(new Date(today.getTime() + 86400000)));
    } else if (preset === 'yesterday') {
      const yesterday = new Date(today.getTime() - 86400000);
      setDateFrom(fmt(yesterday));
      setDateTo(fmt(today));
    } else if (preset === 'week') {
      const weekStart = new Date(today.getTime() - today.getDay() * 86400000);
      setDateFrom(fmt(weekStart));
      setDateTo(fmt(new Date(today.getTime() + 86400000)));
    } else if (preset === 'month') {
      setDateFrom(fmt(new Date(today.getFullYear(), today.getMonth(), 1)));
      setDateTo(fmt(new Date(today.getTime() + 86400000)));
    } else if (preset === 'all') {
      setDateFrom('');
      setDateTo('');
    }
  };

  const handleBulkStatus = async (status) => {
    if (selectedIds.length === 0) { alert('선택된 주문이 없습니다'); return; }
    const label = { ready: '구매준비', buy: '구매완료', cancel: '주문취소' }[status];
    if (!window.confirm(`선택한 ${selectedIds.length}건을 ${label}으로 변경하시겠습니까?`)) return;
    await api.patch('/orders/bulk-status', { ids: selectedIds, status });
    setSelectedIds([]); fetchOrders(); fetchCounts();
  };
  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  const toggleSelectAll = () => setSelectedIds(selectedIds.length === orders.length ? [] : orders.map(o => o.id));
  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadResult(res.data);
      fetchOrders(); fetchCounts();
    } catch (err) { setUploadResult({ message: '업로드 실패', error: true }); }
    finally { setUploading(false); e.target.value = ''; }
  };

  return (
    <div style={{ minWidth: '1000px', maxWidth: '1000px' }}>
      {/* 업로드 결과 팝업 */}
      {uploadResult && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '480px', maxHeight: '70vh', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 24px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '16px', color: '#1a1a1a' }}>엑셀 업로드 결과</div>
            <div style={{ flex: 1, overflowY: 'auto', marginBottom: '16px', border: '0.5px solid #e8e8e8', borderRadius: '8px' }}>
              {uploadResult.error ? (
                <div style={{ padding: '16px', color: '#e53935', fontSize: '13px' }}>업로드 중 오류가 발생했습니다.</div>
              ) : (
                <>
                  {(uploadResult.newOrders || []).map(orderNum => (
                    <div key={orderNum} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderBottom: '0.5px solid #f0f0f0' }}>
                      <span style={{ fontSize: '12px', color: '#1D9E75', fontWeight: '500', flexShrink: 0 }}>✓ 신규</span>
                      <span style={{ fontSize: '12px', color: '#1a1a1a' }}>{orderNum}</span>
                    </div>
                  ))}
                  {(uploadResult.duplicates || []).map(orderNum => (
                    <div key={orderNum} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderBottom: '0.5px solid #f0f0f0', background: '#fffbf0' }}>
                      <span style={{ fontSize: '12px', color: '#EF9F27', fontWeight: '500', flexShrink: 0 }}>⚠ 중복</span>
                      <span style={{ fontSize: '12px', color: '#1a1a1a' }}>{orderNum}</span>
                      <span style={{ fontSize: '11px', color: '#aaa' }}>(건너뜀)</span>
                    </div>
                  ))}
                </>
              )}
            </div>
            <div style={{ fontSize: '13px', color: '#444', marginBottom: '16px', padding: '10px 14px', background: '#f5f5f5', borderRadius: '8px' }}>
              {uploadResult.message}
            </div>
            <button onClick={() => setUploadResult(null)} style={{ fontSize: '13px', padding: '10px', borderRadius: '8px', border: 'none', background: '#1D9E75', color: '#fff', cursor: 'pointer', fontWeight: '500' }}>확인</button>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <h1 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>주문 관리</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label style={{ fontSize: '12px', padding: '6px 14px', borderRadius: '8px', border: '0.5px solid #1D9E75', background: '#E1F5EE', color: '#085041', cursor: 'pointer' }}>
            {uploading ? '업로드 중...' : '엑셀 업로드'}
            <input type="file" accept=".xlsx,.xls" onChange={handleExcelUpload} style={{ display: 'none' }} />
          </label>
        </div>
      </div>
      <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: '12px', padding: '12px 16px', marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '12px', color: '#888', width: '60px', flexShrink: 0 }}>직접검색</span>
          <select value={searchType} onChange={e => setSearchType(e.target.value)} style={{ fontSize: '12px', padding: '5px 8px', borderRadius: '8px', border: '0.5px solid #e8e8e8', background: '#fff' }}>
            <option value="order_number">주문번호</option>
            <option value="product_name">상품명</option>
            <option value="receiver_name">수령인</option>
            <option value="receiver_phone">수령인 연락처</option>
            <option value="buyer_name">구매자</option>
            <option value="address">주소</option>
            <option value="memo">일반관리메모</option>
            <option value="tracking_number">송장번호</option>
            <option value="courier">택배사</option>
          </select>
          {searchType === 'courier' ? (
            <select value={search} onChange={e => { setSearch(e.target.value); setCurrentPage(1); }} style={{ fontSize: '12px', padding: '5px 8px', borderRadius: '8px', border: '0.5px solid #e8e8e8', background: '#fff', width: '200px' }}>
              <option value="">-- 택배사 선택 --</option>
              <option value="CJ">CJ대한통운</option>
              <option value="HANJIN">한진택배</option>
              <option value="LOTTE">롯데택배</option>
              <option value="LOGEN">로젠택배</option>
              <option value="EPOST">우체국택배</option>
              <option value="CHUNILPS">천일택배</option>
              <option value="KURLY">컬리넥스트마일</option>
              <option value="SELF">직접배송</option>
            </select>
          ) : (
            <input value={search} onChange={e => { setSearch(e.target.value); setCurrentPage(1); }} placeholder="검색어 입력" style={{ fontSize: '12px', padding: '5px 10px', borderRadius: '8px', border: '0.5px solid #e8e8e8', width: '200px' }} />
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '12px', color: '#888', width: '60px', flexShrink: 0 }}>판매처</span>
          <select value={platformFilter} onChange={e => { setPlatformFilter(e.target.value); setCurrentPage(1); }} style={{ fontSize: '12px', padding: '5px 8px', borderRadius: '8px', border: '0.5px solid #e8e8e8', background: '#fff' }}>
            <option value="">전체</option>
            {["스마트스토어","쿠팡","옥션","지마켓","11번가","셀러허브","토스쇼핑"].map(p => <option key={p}>{p}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '12px', color: '#888', width: '60px', flexShrink: 0 }}>구매처</span>
          <select value={purchasePlatformFilter} onChange={e => { setPurchasePlatformFilter(e.target.value); setCurrentPage(1); }} style={{ fontSize: '12px', padding: '5px 8px', borderRadius: '8px', border: '0.5px solid #e8e8e8', background: '#fff' }}>
            <option value="">전체</option>
            {purchasePlatforms.map(p => <option key={p.id} value={p.code}>{p.name_kr}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '12px', color: '#888', width: '60px', flexShrink: 0 }}>상태</span>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }} style={{ fontSize: '12px', padding: '5px 8px', borderRadius: '8px', border: '0.5px solid #e8e8e8', background: '#fff' }}>
            <option value="">전체</option>
            {Object.entries(STATUS_CONFIG).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '12px', color: '#888', width: '60px', flexShrink: 0 }}>날짜</span>
          {[
            { key: 'all', label: '전체' },
            { key: 'today', label: '오늘' },
            { key: 'yesterday', label: '어제' },
            { key: 'week', label: '이번주' },
            { key: 'month', label: '이번달' },
          ].map(p => (
            <button key={p.key} onClick={() => applyDatePreset(p.key)} style={{
              fontSize: '12px', padding: '5px 12px', borderRadius: '8px', cursor: 'pointer',
              border: `0.5px solid ${datePreset === p.key || (p.key === 'all' && !datePreset) ? '#1D9E75' : '#e8e8e8'}`,
              background: datePreset === p.key || (p.key === 'all' && !datePreset) ? '#E1F5EE' : '#fff',
              color: datePreset === p.key || (p.key === 'all' && !datePreset) ? '#085041' : '#666',
              fontWeight: datePreset === p.key || (p.key === 'all' && !datePreset) ? '500' : '400',
            }}>{p.label}</button>
          ))}
          <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setDatePreset('custom'); setCurrentPage(1); }}
            style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '8px', border: '0.5px solid #e8e8e8' }} />
          <span style={{ fontSize: '12px', color: '#888' }}>~</span>
          <input type="date" value={dateTo} onChange={e => { const d = new Date(e.target.value); d.setDate(d.getDate()+1); setDateTo(d.toISOString().split('T')[0]); setDatePreset('custom'); setCurrentPage(1); }}
            style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '8px', border: '0.5px solid #e8e8e8' }} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
        <div style={{ background: statusFilter === '' ? '#1D9E75' : '#f5f5f5', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', border: statusFilter === '' ? '0.5px solid #1D9E75' : '0.5px solid transparent' }} onClick={() => { setStatusFilter(''); setCurrentPage(1); }}>
          <div style={{ fontSize: '11px', color: statusFilter === '' ? '#fff' : '#888', marginBottom: '3px' }}>전체</div>
          <div style={{ fontSize: '16px', fontWeight: '600', color: statusFilter === '' ? '#fff' : '#1a1a1a' }}>{totalCount}</div>
        </div>
        {Object.entries(STATUS_CONFIG).map(([k,v]) => (
          <div key={k} style={{ background: statusFilter === k ? v.bg : '#f5f5f5', borderRadius: '8px', padding: '8px 14px', flex: 1, cursor: 'pointer', border: statusFilter === k ? `0.5px solid ${v.color}` : '0.5px solid transparent' }} onClick={() => { setStatusFilter(statusFilter === k ? '' : k); setCurrentPage(1); }}>
            <div style={{ fontSize: '11px', color: '#888', marginBottom: '3px' }}>{v.label}</div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: statusCounts[k] > 0 ? v.color : '#1a1a1a' }}>{statusCounts[k]}</div>
          </div>
        ))}
      </div>
      <OrderFetchButtons onFetchComplete={() => { fetchOrders(); fetchCounts(); }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '12px', color: '#888' }}>페이지당</span>
          <select value={pageLimit} onChange={e => { setPageLimit(parseInt(e.target.value)); setCurrentPage(1); }} style={{ fontSize: '12px', padding: '5px 10px', borderRadius: '8px', border: '0.5px solid #e8e8e8', background: '#fff' }}>
            {[10, 20, 50, 100, 200].map(n => <option key={n} value={n}>{n}건</option>)}
          </select>
          <span style={{ fontSize: '12px', color: '#aaa' }}>총 {total}건</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} style={{ fontSize: '12px', padding: '5px 10px', borderRadius: '8px', border: '0.5px solid #e8e8e8', background: '#fff', cursor: 'pointer', color: currentPage === 1 ? '#ccc' : '#444' }}>{'<<'}</button>
          <button onClick={() => setCurrentPage(p => Math.max(1, p-1))} disabled={currentPage === 1} style={{ fontSize: '12px', padding: '5px 10px', borderRadius: '8px', border: '0.5px solid #e8e8e8', background: '#fff', cursor: 'pointer', color: currentPage === 1 ? '#ccc' : '#444' }}>{'<'}</button>
          {(() => {
            const pages = [];
            const delta = 2;
            const left = currentPage - delta;
            const right = currentPage + delta;
            let last = 0;
            for (let p = 1; p <= totalPages; p++) {
              if (p === 1 || p === totalPages || (p >= left && p <= right)) {
                if (last && p - last > 1) pages.push('...' + p);
                pages.push(p);
                last = p;
              }
            }
            return pages.map((p, i) => {
              if (String(p).startsWith('...')) {
                // key는 페이지 버튼(key={p})과 겹치지 않도록 '...N' 문자열(p)을 쓴다. (index를 쓰면 숫자 key가 버튼과 충돌해 버튼이 중복/순서 꼬임 발생)
                return <span key={p} style={{ fontSize: '12px', color: '#aaa', padding: '0 4px' }}>...</span>;
              }
              return (
                <button key={p} onClick={() => setCurrentPage(p)} style={{ fontSize: '12px', padding: '5px 10px', borderRadius: '8px', border: '0.5px solid #e8e8e8', background: currentPage === p ? '#1D9E75' : '#fff', color: currentPage === p ? '#fff' : '#1a1a1a', cursor: 'pointer', minWidth: '34px' }}>{p}</button>
              );
            });
          })()}
          <button onClick={() => setCurrentPage(p => Math.min(totalPages, p+1))} disabled={currentPage === totalPages} style={{ fontSize: '12px', padding: '5px 10px', borderRadius: '8px', border: '0.5px solid #e8e8e8', background: '#fff', cursor: 'pointer', color: currentPage === totalPages ? '#ccc' : '#444' }}>{'>'}</button>
          <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} style={{ fontSize: '12px', padding: '5px 10px', borderRadius: '8px', border: '0.5px solid #e8e8e8', background: '#fff', cursor: 'pointer', color: currentPage === totalPages ? '#ccc' : '#444' }}>{'>>'}</button>
        </div>
      </div>
      {orders.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', padding: '6px 10px', background: '#f9f9f9', borderRadius: '8px', border: '0.5px solid #e8e8e8' }}>
          <input type="checkbox" checked={selectedIds.length === orders.length && orders.length > 0} onChange={toggleSelectAll} style={{ width: '15px', height: '15px', accentColor: '#1D9E75', cursor: 'pointer' }} />
          <span style={{ fontSize: '12px', color: '#666' }}>전체 선택</span>
          {selectedIds.length > 0 && <span style={{ fontSize: '12px', color: '#1D9E75', fontWeight: '500' }}>{selectedIds.length}건 선택됨</span>}
        </div>
      )}
      {orders.length === 0 ? (
        <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: '12px', padding: '40px', textAlign: 'center', color: '#aaa', fontSize: '14px' }}>주문 데이터가 없습니다</div>
      ) : (
        orders.map((order, i) => (
          <OrderCard key={order.id} order={order} orderIndex={i} totalOrders={total} currentPage={currentPage} pageLimit={pageLimit} cards={cards} platforms={platforms} accounts={accounts} couriers={couriers} trackerClientId={trackerClientId}
            isSelected={selectedIds.includes(order.id)} onSelect={() => toggleSelect(order.id)}
            onStatusChange={handleStatusChange} onPurchaseSave={handlePurchaseSave} onRefundSave={() => { fetchOrders(); fetchCounts(); }} />
        ))
      )}
      <div style={{ display: 'flex', gap: '8px', padding: '12px 16px', background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: '10px', marginTop: '12px', alignItems: 'center' }}>
        <span style={{ fontSize: '12px', color: '#666', marginRight: '4px' }}>{selectedIds.length > 0 ? `선택한 ${selectedIds.length}건:` : '주문을 선택하세요:'}</span>
        <button onClick={() => handleBulkStatus('ready')} style={{ fontSize: '12px', padding: '6px 16px', borderRadius: '8px', border: '0.5px solid #534AB7', background: '#EEEDFE', color: '#3C3489', cursor: 'pointer', fontWeight: '500' }}>선택한 주문 구매준비 처리</button>
        <button onClick={() => handleBulkStatus('hold')} style={{ fontSize: '12px', padding: '6px 16px', borderRadius: '8px', border: '0.5px solid #EF9F27', background: '#FAEEDA', color: '#854F0B', cursor: 'pointer', fontWeight: '500' }}>선택한 주문 구매보류 처리</button>
        <button onClick={() => handleBulkStatus('buy')} style={{ fontSize: '12px', padding: '6px 16px', borderRadius: '8px', border: '0.5px solid #888780', background: '#F1EFE8', color: '#444441', cursor: 'pointer', fontWeight: '500' }}>선택한 주문 구매완료 처리</button>
        <button onClick={() => handleBulkStatus('cancel')} style={{ fontSize: '12px', padding: '6px 16px', borderRadius: '8px', border: '0.5px solid #E24B4A', background: '#FCEBEB', color: '#A32D2D', cursor: 'pointer', fontWeight: '500' }}>선택한 주문 주문취소 처리</button>
      </div>
        <DeliveryTrackButton />
      <InvoiceSendPanel pendingCounts={invoicePendingCounts} onSendComplete={() => { fetchInvoicePendingCounts(); fetchOrders(); }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '12px', color: '#888' }}>페이지당</span>
          <select value={pageLimit} onChange={e => { setPageLimit(parseInt(e.target.value)); setCurrentPage(1); }} style={{ fontSize: '12px', padding: '5px 10px', borderRadius: '8px', border: '0.5px solid #e8e8e8', background: '#fff' }}>
            {[10, 20, 50, 100, 200].map(n => <option key={n} value={n}>{n}건</option>)}
          </select>
          <span style={{ fontSize: '12px', color: '#aaa' }}>총 {total}건</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} style={{ fontSize: '12px', padding: '5px 10px', borderRadius: '8px', border: '0.5px solid #e8e8e8', background: '#fff', cursor: 'pointer', color: currentPage === 1 ? '#ccc' : '#444' }}>{'<<'}</button>
          <button onClick={() => setCurrentPage(p => Math.max(1, p-1))} disabled={currentPage === 1} style={{ fontSize: '12px', padding: '5px 10px', borderRadius: '8px', border: '0.5px solid #e8e8e8', background: '#fff', cursor: 'pointer', color: currentPage === 1 ? '#ccc' : '#444' }}>{'<'}</button>
          {(() => {
            const pages = [];
            const delta = 2;
            const left = currentPage - delta;
            const right = currentPage + delta;
            let last = 0;
            for (let p = 1; p <= totalPages; p++) {
              if (p === 1 || p === totalPages || (p >= left && p <= right)) {
                if (last && p - last > 1) pages.push('...' + p);
                pages.push(p);
                last = p;
              }
            }
            return pages.map((p, i) => {
              if (String(p).startsWith('...')) {
                // key는 페이지 버튼(key={p})과 겹치지 않도록 '...N' 문자열(p)을 쓴다. (index를 쓰면 숫자 key가 버튼과 충돌해 버튼이 중복/순서 꼬임 발생)
                return <span key={p} style={{ fontSize: '12px', color: '#aaa', padding: '0 4px' }}>...</span>;
              }
              return (
                <button key={p} onClick={() => setCurrentPage(p)} style={{ fontSize: '12px', padding: '5px 10px', borderRadius: '8px', border: '0.5px solid #e8e8e8', background: currentPage === p ? '#1D9E75' : '#fff', color: currentPage === p ? '#fff' : '#1a1a1a', cursor: 'pointer', minWidth: '34px' }}>{p}</button>
              );
            });
          })()}
          <button onClick={() => setCurrentPage(p => Math.min(totalPages, p+1))} disabled={currentPage === totalPages} style={{ fontSize: '12px', padding: '5px 10px', borderRadius: '8px', border: '0.5px solid #e8e8e8', background: '#fff', cursor: 'pointer', color: currentPage === totalPages ? '#ccc' : '#444' }}>{'>'}</button>
          <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} style={{ fontSize: '12px', padding: '5px 10px', borderRadius: '8px', border: '0.5px solid #e8e8e8', background: '#fff', cursor: 'pointer', color: currentPage === totalPages ? '#ccc' : '#444' }}>{'>>'}</button>
        </div>
      </div>

      {/* 최상단/최하단 이동 버튼 */}
      <div style={{ position: 'fixed', left: '1210px', top: '50%', transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', gap: '8px', zIndex: 999 }}>
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{ width: '40px', height: '40px', borderRadius: '50%', border: '0.5px solid #e8e8e8', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', fontSize: '14px', color: '#666' }}>
          ▲
        </button>
        <button onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}
          style={{ width: '40px', height: '40px', borderRadius: '50%', border: '0.5px solid #e8e8e8', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', fontSize: '14px', color: '#666' }}>
          ▼
        </button>
      </div>
    </div>
  );
}
